<script setup lang="ts">
import { computed, onBeforeUnmount, ref, useTemplateRef, type PropType } from 'vue';
import GaiaBreadcrumb from '../domain/navigation/GaiaBreadcrumb.vue';
import GaiaTopbar from '../domain/navigation/GaiaTopbar.vue';
import {
  gaiaAccent,
  gaiaBaseTextSize,
  gaiaMotion,
  gaiaNeutral,
  gaiaShellPadding,
} from '../themes/tokens';

const props = defineProps({
  /**
   * Right drawer open state — a plain prop, the parent view owns the toggle,
   * as in layout/Layout.vue. The drawer is a track of the pane column: opening
   * it narrows the panes rather than covering them, because what you consult a
   * chat about is usually what it would otherwise hide. It never reaches the
   * rail beside that column.
   */
  drawerOpen: {
    type: Boolean,
    default: false,
  },
  drawerWidth: {
    type: [String, Number] as PropType<string | number>,
    default: 360,
  },
  /**
   * The drawer has taken the pane column, so the panes step aside — the same
   * trade the stacked panes make when one of them expands over the analysis.
   * Its resting width is a track beside them; this is for work that wants the
   * whole column (reading a long answer, scanning a transcript).
   */
  drawerExpanded: {
    type: Boolean,
    default: false,
  },
  /**
   * Padding around the #title block. The body below it is flush to the sidebar
   * on the left and inset from the window's right and bottom edges by
   * `gaiaShellPadding.gutter`, which puts its panes' content on this same edge.
   */
  titlePadding: {
    type: String,
    default: `22px ${gaiaShellPadding.pageX} 14px`,
  },
  /**
   * Width of the #rail column, and what it shrinks to once collapsed. The
   * collapsed step is wide enough for the pane's expand control and its label
   * set on its side (GaiaWorkspacePane's rail state).
   */
  railWidth: {
    type: [String, Number] as PropType<string | number>,
    default: 344,
  },
  railCollapsedWidth: {
    type: [String, Number] as PropType<string | number>,
    default: 52,
  },
  /** The rail is showing its collapsed state, so the shell narrows its track. */
  railCollapsed: {
    type: Boolean,
    default: false,
  },
  /**
   * Mounts a drag handle on the rail's seam. The width itself stays the
   * caller's: it emits `update:railWidth` in px, so the page decides whether to
   * persist the choice (`v-model:rail-width` over a stored ref) or forget it.
   */
  railResizable: {
    type: Boolean,
    default: false,
  },
  /**
   * Drag limits, in px. The floor is the narrowest the rail's own content still
   * works at; the ceiling keeps the rail from swallowing the panes beside it.
   */
  railMinWidth: {
    type: Number,
    default: 260,
  },
  railMaxWidth: {
    type: Number,
    default: 640,
  },
  /** What the handle is called to a screen reader — name the rail it moves. */
  railResizeLabel: {
    type: String,
    default: 'Resize the side panel',
  },
});

const emit = defineEmits<{ 'update:railWidth': [width: number] }>();

function trackWidth(value: string | number): string {
  return typeof value === 'number' ? `${value}px` : value;
}

/* ------------------------------------------------------------ rail resize */

const railEl = useTemplateRef<HTMLElement>('railEl');
const dragging = ref(false);
/** Pointer x and rail width at the moment the drag started. */
let startX = 0;
let startWidth = 0;
/** Where the drag is now — the value painted each frame and emitted at the end. */
let draggedWidth = 0;
/** Pending paint, so several pointer moves in one frame cost one write. */
let paintFrame: number | null = null;
/** The handle holding pointer capture, so the drag can always be released. */
let capturedHandle: HTMLElement | null = null;
let capturedPointerId: number | null = null;
/** One arrow press; Shift multiplies it, as in every other resizer. */
const KEYBOARD_STEP = 16;
const KEYBOARD_STEP_COARSE = 64;

/** The rail's width as a number, whether the prop came as px or as a CSS length. */
const railWidthPx = computed(() =>
  typeof props.railWidth === 'number'
    ? props.railWidth
    : Number.parseFloat(props.railWidth) || props.railMinWidth
);

function clampWidth(px: number): number {
  return Math.round(Math.min(Math.max(px, props.railMinWidth), props.railMaxWidth));
}

/**
 * Measured rather than read off `railWidth`: that prop also takes a CSS string,
 * and the rendered track is the only thing the delta can be relative to.
 */
function currentWidth(): number {
  return railEl.value?.offsetWidth ?? props.railMinWidth;
}

function cancelPaint(): void {
  if (paintFrame !== null) {
    cancelAnimationFrame(paintFrame);
    paintFrame = null;
  }
}

function paint(): void {
  paintFrame = null;
  if (railEl.value) railEl.value.style.width = `${draggedWidth}px`;
}

function onHandleDown(event: PointerEvent): void {
  // Cancelling pointerdown suppresses the compatibility mousedown, and with it
  // the text selection the browser would otherwise sweep across the panes as
  // the pointer travels.
  event.preventDefault();
  const handle = event.currentTarget as HTMLElement;
  // Focus is one of the defaults just cancelled, and deliberately not restored:
  // Chrome counts a programmatic focus() as :focus-visible, so a mouse drag
  // would leave the seam lit as if it were the keyboard's target until focus
  // moved elsewhere. Tabbing to the handle still focuses it — and there
  // :focus-visible is exactly right.
  // Capture so the drag survives the pointer leaving the 7px handle — without
  // it a fast drag stops the moment the cursor outruns the rail.
  handle.setPointerCapture(event.pointerId);
  capturedHandle = handle;
  capturedPointerId = event.pointerId;
  dragging.value = true;
  startX = event.clientX;
  startWidth = currentWidth();
  draggedWidth = startWidth;
}

function onHandleMove(event: PointerEvent): void {
  if (!dragging.value) return;
  draggedWidth = clampWidth(startWidth + (event.clientX - startX));
  /*
   * Written straight to the element and coalesced to one write per frame,
   * rather than emitted. The width belongs to the page, so routing every
   * pointer move through the prop re-renders that whole page — and with it
   * re-patches every list row, table and anchored popover in the workspace,
   * on top of the reflow the resize already costs. That is what makes a drag
   * crawl. Vue writes the same value back through the `:style` binding when
   * the settled width lands, so the imperative write is never the source of
   * truth for longer than the drag.
   */
  paintFrame ??= requestAnimationFrame(paint);
}

/**
 * Ends the drag from any of its exits — pointerup, cancel, or capture being
 * lost (the element re-created under it, the tab losing focus). Without the
 * lost-capture route a dropped pointerup leaves the workspace stuck in its
 * dragging state, unable to select text.
 */
function endDrag(): void {
  if (!dragging.value) return;
  cancelPaint();
  paint();
  dragging.value = false;
  if (capturedHandle && capturedPointerId !== null) {
    if (capturedHandle.hasPointerCapture(capturedPointerId)) {
      capturedHandle.releasePointerCapture(capturedPointerId);
    }
    capturedHandle = null;
    capturedPointerId = null;
  }
  emit('update:railWidth', draggedWidth);
}

function nudge(direction: -1 | 1, coarse: boolean): void {
  const step = coarse ? KEYBOARD_STEP_COARSE : KEYBOARD_STEP;
  emit('update:railWidth', clampWidth(currentWidth() + direction * step));
}

// A frame queued as the view unmounts would paint a detached element.
onBeforeUnmount(cancelPaint);

// Page-level body size, as in layout/Layout.vue.
const baseTextSize = gaiaBaseTextSize;
const bodyBorder = gaiaNeutral.borderMuted;
const railMotion = gaiaMotion.morph;
const seamActive = gaiaAccent.base;
const bodyGutter = gaiaShellPadding.gutter;
/**
 * Closed it is a zero-width track (so opening and closing is one transition on
 * one property), open it is its stated width, and expanded it is the column —
 * which it can take outright, since the panes are not rendered beside it then.
 *
 * The cap is what stops a fixed-width drawer from starving the panes on a
 * narrow window: it would otherwise take its 360px out of a 400px column and
 * leave them 40. Lifted while expanded, where taking the column is the point.
 */
const drawerStyle = computed(() => {
  if (!props.drawerOpen) return { width: '0', maxWidth: '0' };
  return props.drawerExpanded
    ? { width: '100%', maxWidth: '100%' }
    : { width: trackWidth(props.drawerWidth), maxWidth: '60%' };
});
</script>

<template>
  <!-- The workspace shell (Review Projects mock, project detail): the
       breadcrumb bar (from #breadcrumb, as in layout/Layout.vue), a title
       block, and a body that fills the rest of the viewport without
       scrolling — the panes inside it scroll independently.
       Structurally the counterpart to layout/Layout.vue: that one centres a
       capped reading column and lets the page scroll, which a pane grid can't
       use. Pane chrome comes entirely from the slots (GaiaWorkspacePane), and
       the sider is the same drawer channel Layout.vue exposes. -->
  <n-layout
    has-sider
    position="absolute"
    class="gaia-workspace-layout"
    :style="{ fontSize: baseTextSize }"
  >
    <!-- No inner n-layout: the body must be a flex child with a definite
         height, and naive's layout wraps its content in a scroll container
         (same reason WizardLayout builds its column by hand). -->
    <div class="gaia-workspace-layout__column">
      <gaia-topbar v-if="$slots.breadcrumb" class="gaia-workspace-layout__header">
        <gaia-breadcrumb>
          <slot name="breadcrumb" />
        </gaia-breadcrumb>
      </gaia-topbar>
      <div
        v-if="$slots.title"
        class="gaia-workspace-layout__title"
        :style="{ padding: titlePadding }"
      >
        <slot name="title" />
      </div>
      <div class="gaia-workspace-layout__body" :class="{ 'is-resizing': dragging }">
        <!-- The rail is a track of the body, not a naive sider: it collapses to
             a legible 52px stub rather than to nothing, and the pane inside it
             owns that stub's chrome. -->
        <div
          v-if="$slots.rail"
          ref="railEl"
          class="gaia-workspace-layout__rail"
          :class="{ 'is-resizing': dragging }"
          :style="{
            width: trackWidth(railCollapsed ? railCollapsedWidth : railWidth),
          }"
        >
          <slot name="rail" />
        </div>
        <!-- Sits on the seam and costs it no width: the line the user grabs is
             the rail pane's own border, and the handle only widens the hit area
             and lights the line up. Collapsed there is nothing to resize — the
             expand control is the affordance then. -->
        <div
          v-if="$slots.rail && railResizable && !railCollapsed"
          class="gaia-workspace-layout__seam"
          :class="{ 'is-resizing': dragging }"
          role="separator"
          aria-orientation="vertical"
          :aria-label="railResizeLabel"
          :aria-valuenow="Math.round(railWidthPx)"
          :aria-valuemin="railMinWidth"
          :aria-valuemax="railMaxWidth"
          tabindex="0"
          draggable="false"
          @dragstart.prevent
          @pointerdown="onHandleDown"
          @pointermove="onHandleMove"
          @pointerup="endDrag"
          @pointercancel="endDrag"
          @lostpointercapture="endDrag"
          @keydown.left.prevent="(e: KeyboardEvent) => nudge(-1, e.shiftKey)"
          @keydown.right.prevent="(e: KeyboardEvent) => nudge(1, e.shiftKey)"
        />
        <!-- The drawer is a track of this column, not of the body: it narrows
             the panes and never the rail beside them. -->
        <div class="gaia-workspace-layout__panes">
          <div v-if="!drawerExpanded" class="gaia-workspace-layout__panes-stack">
            <slot />
          </div>

          <!-- Stays mounted and animates its width: the drawer holds live state
               (a chat's thread and unsent draft), and unmounting it on close
               would throw that away every time it is dismissed. `inert` is what
               makes a closed drawer truly gone — a zero-width track still
               leaves its controls in the tab order. -->
          <aside
            v-if="$slots.drawer"
            class="gaia-workspace-layout__drawer"
            :style="drawerStyle"
            :inert="!drawerOpen"
            :aria-hidden="!drawerOpen"
          >
            <slot name="drawer" />
          </aside>
        </div>
      </div>
    </div>
  </n-layout>
</template>

<style scoped>
.gaia-workspace-layout__column {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
}

.gaia-workspace-layout__header,
.gaia-workspace-layout__title {
  flex: none;
}

/* The pane grid: fills what the header and title leave, and never scrolls as a
   whole — a hairline separates it from the title block above.

   The gutter on the two free edges is the shell's, not a pane's: it holds every
   pane off the window frame at once, and a pane setting its own would put the
   gap inside its border instead of outside it. Left stays flush — the app
   sidebar is the edge there. */
.gaia-workspace-layout__body {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  min-width: 0;
  padding: 0 v-bind(bodyGutter) v-bind(bodyGutter) 0;
  border-top: 1px solid v-bind(bodyBorder);
}

/* `is-resizing`, not `dragging`: contractManagement/QueryGroup.vue publishes an
   unscoped `:global(.dragging) { opacity: .4; transform: scale(.95); box-shadow: … }`,
   which claims that class name app-wide — a workspace marked `dragging` faded
   and shrank into a card for as long as the pointer was down. Scoping does not
   protect against it: the global rule matches this element too, and the
   properties it sets are ones this file never declares.

   No text selection anywhere in the body while a drag is live — pointer capture
   keeps the events coming, but the pointer still sweeps over the panes' copy. */
.gaia-workspace-layout__body.is-resizing {
  user-select: none;
}

.gaia-workspace-layout__rail {
  flex: none;
  min-width: 0;
  display: flex;
  flex-direction: column;
  transition: width v-bind(railMotion);
}

/* The collapse animation must not run during a drag: easing every pointermove
   leaves the rail lagging a cursor it is supposed to be pinned to. */
.gaia-workspace-layout__rail.is-resizing {
  transition: none;
}

.gaia-workspace-layout__seam {
  flex: none;
  width: 0;
  position: relative;
  z-index: 3;
  cursor: col-resize;
  /* Stops the browser panning/scrolling instead of reporting the drag. */
  touch-action: none;
  /* No text to select here, and a selection starting on the handle is what the
     browser would otherwise offer to drag. */
  user-select: none;
}

/* The hit area: wider than the 1px line it moves, and straddling it so the
   grab starts before the pointer is exactly on the seam. */
.gaia-workspace-layout__seam::before {
  content: '';
  position: absolute;
  inset: 0 -3px;
}

/* The seam itself lights up under the pointer (and on keyboard focus), which is
   the only thing telling the user the border is draggable. */
.gaia-workspace-layout__seam::after {
  content: '';
  position: absolute;
  inset: 0 -1px;
  background: transparent;
  transition: background 0.15s;
}

.gaia-workspace-layout__seam:hover::after,
.gaia-workspace-layout__seam:focus-visible::after,
.gaia-workspace-layout__seam.is-resizing::after {
  background: v-bind(seamActive);
}

.gaia-workspace-layout__seam:focus-visible {
  outline: none;
}

/* Stacks the panes the rail sits beside — analysis over report.
   `auto`, not `hidden`: a stack of panes each declaring a min-height can want
   more room than the column has (analysis 360 + report 300 on a short window),
   and flexing past those floors is what squeezes the top pane down to a single
   visible row. Scrolling the column keeps both usable instead. With one pane it
   flexes to fit and no scrollbar appears. */
/* A row now: the pane stack and the drawer are its two tracks. */
.gaia-workspace-layout__panes {
  flex: 1;
  min-width: 0;
  min-height: 0;
  display: flex;
  overflow: hidden;
}

.gaia-workspace-layout__panes-stack {
  flex: 1;
  min-width: 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
}

/* No shadow: nothing is being covered. The panel inside rules off against the
   panes with its own border, the way the rail on the other side does. */
.gaia-workspace-layout__drawer {
  flex: none;
  min-width: 0;
  display: flex;
  flex-direction: column;
  /* Clips the content while the track is closing, so it never spills left. */
  overflow: hidden;
  transition: width v-bind(railMotion);
}
</style>
